import Button from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';
import LoadIndicator from 'devextreme-react/load-indicator';
import SelectBox from 'devextreme-react/select-box';
import TextBox from 'devextreme-react/text-box';
import CustomStore from 'devextreme/data/custom_store';
import { useEffect, useMemo, useRef, useState } from 'react';
import { showToast, normalizeSmbUncPath } from '@jarvis/platform-ui';
import type {
  DirectoryPrincipal,
  StorageAccessMode,
  StorageMountKind,
  UpsertAccessRequest
} from '../../../api/storageMountsAdmin';
import { searchGroups, testMountConnection } from '../../../api/storageMountsAdmin';
import {
  listClientPortalTenants,
  searchClientPortalGroups,
  type ClientPortalTenantOption
} from '../../../api/clientPortalStorageMountsAdmin';
import {
  listClientSpaceRoles,
  provisionClientSpace,
  type ProvisionClientSpaceResult
} from '../../../api/clientSpaceProvisioning';
import './clientSpaceWizard.css';

type AccessRow = UpsertAccessRequest & { _key: string };

const STEPS = ['Client', 'Storage', 'Ops access', 'Client access', 'Review'] as const;

const MODE_ITEMS: { value: StorageAccessMode; text: string }[] = [
  { value: 'Read', text: 'Read' },
  { value: 'ReadWrite', text: 'Read/Write' }
];

/** Typical WebOps mount ACL. Omit group → Any group. Rows can be deleted in the wizard. */
function defaultOpsAccess(): AccessRow[] {
  return [
    { _key: crypto.randomUUID(), role: 'ItSysAdmin', mode: 'ReadWrite' },
    { _key: crypto.randomUUID(), role: 'OpsFinance', mode: 'ReadWrite' },
    { _key: crypto.randomUUID(), role: 'OpsSales', mode: 'Read' }
  ];
}

interface SmbForm {
  uncPath: string;
  domain: string;
  username: string;
  password: string;
}

interface S3Form {
  serviceUrl: string;
  region: string;
  bucketName: string;
  accessKeyId: string;
  secretAccessKey: string;
  keyPrefix: string;
  forcePathStyle: boolean;
}

function serializeSettings(kind: StorageMountKind, smb: SmbForm, s3: S3Form): string {
  if (kind === 'Smb') {
    return JSON.stringify({
      uncPath: normalizeSmbUncPath(smb.uncPath),
      domain: smb.domain.trim(),
      username: smb.username.trim(),
      password: smb.password
    });
  }
  return JSON.stringify({
    serviceUrl: s3.serviceUrl.trim(),
    region: s3.region.trim(),
    bucketName: s3.bucketName.trim(),
    accessKeyId: s3.accessKeyId.trim(),
    secretAccessKey: s3.secretAccessKey,
    keyPrefix: s3.keyPrefix.trim(),
    forcePathStyle: s3.forcePathStyle
  });
}

/** Role + optional Entra group + mode rows, reused for the two permission steps. */
function AccessEditor({
  roles,
  rows,
  onChange,
  groupStore,
  groupCache,
  groupPlaceholder,
  emptyHint
}: {
  roles: string[];
  rows: AccessRow[];
  onChange: (rows: AccessRow[]) => void;
  groupStore: CustomStore;
  groupCache: Map<string, DirectoryPrincipal>;
  groupPlaceholder: string;
  emptyHint: string;
}) {
  const [role, setRole] = useState('');
  const [mode, setMode] = useState<StorageAccessMode>('Read');
  const [group, setGroup] = useState<DirectoryPrincipal | null>(null);

  function addRow() {
    if (!role) return;
    onChange([
      ...rows,
      {
        _key: crypto.randomUUID(),
        role,
        groupObjectId: group?.id ?? undefined,
        groupDisplayName: group?.displayName ?? undefined,
        mode
      }
    ]);
    setRole('');
    setMode('Read');
    setGroup(null);
  }

  return (
    <div>
      {rows.length === 0 ? (
        <p className="client-space-wizard__hint" style={{ marginBottom: 12 }}>{emptyHint}</p>
      ) : (
        <div style={{ marginBottom: 12 }}>
          {rows.map((r) => (
            <div key={r._key} className="client-space-wizard__access-list-row">
              <span style={{ flex: 1 }}>{r.role}</span>
              <span style={{ flex: 2, fontSize: 12, color: 'var(--ops-color-muted, #6b7280)' }}>
                {r.groupDisplayName ?? 'Any group'}
              </span>
              <span style={{ fontSize: 12 }}>{r.mode}</span>
              <Button
                icon="trash"
                stylingMode="text"
                type="danger"
                onClick={() => onChange(rows.filter((x) => x._key !== r._key))}
              />
            </div>
          ))}
        </div>
      )}

      <div className="client-space-wizard__access-row">
        <div>
          <span className="client-space-wizard__field-label">Role</span>
          <SelectBox
            dataSource={roles}
            value={role || null}
            onValueChanged={(e) => setRole((e.value as string) ?? '')}
            placeholder="Select role…"
            showClearButton
          />
        </div>
        <div>
          <span className="client-space-wizard__field-label">Entra group (optional)</span>
          <SelectBox
            dataSource={groupStore}
            displayExpr="displayName"
            valueExpr="id"
            searchEnabled
            minSearchLength={2}
            searchTimeout={300}
            placeholder={groupPlaceholder}
            noDataText="No groups found — type at least 2 characters"
            value={group?.id ?? null}
            onValueChanged={(e) => {
              const id = e.value as string | null | undefined;
              setGroup(id ? groupCache.get(id) ?? null : null);
            }}
            showClearButton
          />
        </div>
        <div>
          <span className="client-space-wizard__field-label">Mode</span>
          <SelectBox
            dataSource={MODE_ITEMS}
            displayExpr="text"
            valueExpr="value"
            value={mode}
            onValueChanged={(e) => setMode(e.value as StorageAccessMode)}
            width={140}
          />
        </div>
        <Button icon="plus" text="Add" onClick={addRow} disabled={!role} />
      </div>
    </div>
  );
}

export function ClientSpaceWizardPage() {
  const [step, setStep] = useState(0);
  const [tenants, setTenants] = useState<ClientPortalTenantOption[]>([]);
  const [opsRoles, setOpsRoles] = useState<string[]>([]);
  const [clientRoles, setClientRoles] = useState<string[]>([]);

  const [tenantId, setTenantId] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [opsDisplayName, setOpsDisplayName] = useState('');

  const [kind, setKind] = useState<StorageMountKind>('S3');
  const [smb, setSmb] = useState<SmbForm>({ uncPath: '', domain: '', username: '', password: '' });
  const [s3, setS3] = useState<S3Form>({
    serviceUrl: '',
    region: '',
    bucketName: '',
    accessKeyId: '',
    secretAccessKey: '',
    keyPrefix: '',
    forcePathStyle: true
  });

  const [opsAccess, setOpsAccess] = useState<AccessRow[]>(defaultOpsAccess);
  const [clientAccess, setClientAccess] = useState<AccessRow[]>([]);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ProvisionClientSpaceResult | null>(null);

  const opsGroupCache = useRef(new Map<string, DirectoryPrincipal>());
  const clientGroupCache = useRef(new Map<string, DirectoryPrincipal>());

  useEffect(() => {
    void listClientPortalTenants().then(setTenants).catch((e: unknown) =>
      showToast(e instanceof Error ? e.message : String(e), 'error')
    );
    void listClientSpaceRoles()
      .then((r) => {
        setOpsRoles(r.opsRoles);
        setClientRoles(r.clientRoles);
      })
      .catch((e: unknown) => showToast(e instanceof Error ? e.message : String(e), 'error'));
  }, []);

  const opsGroupStore = useMemo(
    () =>
      new CustomStore({
        key: 'id',
        load: async (options) => {
          const q = String(options.searchValue ?? '').trim();
          if (q.length < 2) return [];
          try {
            const results = await searchGroups(q);
            for (const g of results) opsGroupCache.current.set(g.id, g);
            return results;
          } catch {
            return [];
          }
        },
        byKey: async (key) =>
          opsGroupCache.current.get(String(key)) ?? {
            id: String(key),
            displayName: String(key),
            mail: null,
            userPrincipalName: null
          }
      }),
    []
  );

  const clientGroupStore = useMemo(
    () =>
      new CustomStore({
        key: 'id',
        load: async (options) => {
          const q = String(options.searchValue ?? '').trim();
          if (q.length < 2 || !tenantId) return [];
          try {
            const results = await searchClientPortalGroups(tenantId, q);
            for (const g of results) clientGroupCache.current.set(g.id, g);
            return results;
          } catch {
            return [];
          }
        },
        byKey: async (key) =>
          clientGroupCache.current.get(String(key)) ?? {
            id: String(key),
            displayName: String(key),
            mail: null,
            userPrincipalName: null
          }
      }),
    [tenantId]
  );

  const connectionComplete =
    kind === 'Smb'
      ? Boolean(smb.uncPath.trim() && smb.username.trim() && smb.password)
      : Boolean(s3.bucketName.trim() && s3.accessKeyId.trim() && s3.secretAccessKey);

  const canAdvance = (() => {
    switch (step) {
      case 0:
        return Boolean(tenantId && displayName.trim());
      case 1:
        return connectionComplete;
      default:
        return true;
    }
  })();

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await testMountConnection({
        kind,
        settingsJson: serializeSettings(kind, smb, s3)
      });
      setTestResult({
        success: r.success,
        message: r.success
          ? `Connected — ${r.itemCount ?? 0} item${r.itemCount === 1 ? '' : 's'} at root.`
          : r.error ?? 'Connection failed.'
      });
    } catch (e: unknown) {
      setTestResult({ success: false, message: e instanceof Error ? e.message : String(e) });
    } finally {
      setTesting(false);
    }
  }

  async function handleSubmit() {
    if (!tenantId) return;
    setSubmitting(true);
    try {
      const created = await provisionClientSpace({
        tenantId,
        displayName: displayName.trim(),
        opsDisplayName: opsDisplayName.trim() || null,
        kind,
        settingsJson: serializeSettings(kind, smb, s3),
        opsAccess: opsAccess.map(({ _key: _k, ...rest }) => rest),
        clientAccess: clientAccess.map(({ _key: _k, ...rest }) => rest)
      });
      setResult(created);
      showToast('Client space created on both sites.', 'success');
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : String(e), 'error');
    } finally {
      setSubmitting(false);
    }
  }

  function startAnother() {
    setResult(null);
    setStep(0);
    setTenantId(null);
    setDisplayName('');
    setOpsDisplayName('');
    setSmb({ uncPath: '', domain: '', username: '', password: '' });
    setS3({
      serviceUrl: '',
      region: '',
      bucketName: '',
      accessKeyId: '',
      secretAccessKey: '',
      keyPrefix: '',
      forcePathStyle: true
    });
    setOpsAccess(defaultOpsAccess());
    setClientAccess([]);
    setTestResult(null);
  }

  const tenantLabel = tenants.find((t) => t.tenantId === tenantId)?.displayName ?? tenantId ?? '—';

  if (result) {
    return (
      <div className="ops-fill-page">
        <div className="client-space-wizard">
          <header className="client-space-wizard__header">
            <h2 className="client-space-wizard__title">New client space</h2>
          </header>
        <div className="client-space-wizard__result">
          <p style={{ marginTop: 0, fontWeight: 600 }}>
            Created and linked {displayName} on both sites.
          </p>
          <dl className="client-space-wizard__review">
            <dt>Client Portal mount</dt>
            <dd>{result.clientMountId}</dd>
            <dt>WebOps mount</dt>
            <dd>{result.opsMountId}</dd>
            <dt>Shared file review storage key</dt>
            <dd>{result.fileReviewStorageKey}</dd>
          </dl>
          <p className="client-space-wizard__hint" style={{ marginBottom: 12 }}>
            Both mounts point at the same storage and reference each other, so @mentions on either side
            resolve to the client Entra groups you picked.
          </p>
          <Button text="Create another" type="default" onClick={startAnother} />
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ops-fill-page">
      <div className="client-space-wizard">
        <header className="client-space-wizard__header">
          <h2 className="client-space-wizard__title">New client space</h2>
          <p className="client-space-wizard__subtitle">
            Creates the Client Portal mount and the matching WebOps mount in one pass, and links them so
            @mentions resolve to the client&apos;s Entra groups from both sides.
          </p>
        </header>

      <ol className="client-space-wizard__steps">
        {STEPS.map((label, i) => (
          <li
            key={label}
            className={[
              'client-space-wizard__step',
              i === step ? 'client-space-wizard__step--active' : '',
              i < step ? 'client-space-wizard__step--done' : ''
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {i + 1}. {label}
          </li>
        ))}
      </ol>

      <div className="client-space-wizard__panel">
        {step === 0 && (
          <>
            <div>
              <span className="client-space-wizard__field-label">Client tenant</span>
              <SelectBox
                dataSource={tenants}
                displayExpr="displayName"
                valueExpr="tenantId"
                value={tenantId}
                onValueChanged={(e) => setTenantId((e.value as string | null) ?? null)}
                placeholder="Select a configured client tenant…"
                noDataText="No tenants configured under ClientGraph:Tenants"
              />
              <span className="client-space-wizard__hint">
                Only tenants with Graph credentials in this environment appear here.
              </span>
            </div>
            <div className="client-space-wizard__grid-2">
              <div>
                <span className="client-space-wizard__field-label">Mount name</span>
                <TextBox
                  value={displayName}
                  onValueChanged={(e) => setDisplayName(String(e.value ?? ''))}
                  placeholder="e.g. DEMOCE1"
                />
                <span className="client-space-wizard__hint">Shown in the client&apos;s file browser.</span>
              </div>
              <div>
                <span className="client-space-wizard__field-label">WebOps name (optional)</span>
                <TextBox
                  value={opsDisplayName}
                  onValueChanged={(e) => setOpsDisplayName(String(e.value ?? ''))}
                  placeholder="Defaults to the mount name"
                />
              </div>
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <p className="client-space-wizard__hint" style={{ marginTop: 0 }}>
              Both sites get the same connection, so enter it once. A shared file review key is generated
              automatically so comments and mentions treat it as one location.
            </p>
            <div>
              <span className="client-space-wizard__field-label">Storage type</span>
              <SelectBox
                dataSource={[
                  { value: 'S3', text: 'S3 / MinIO' },
                  { value: 'Smb', text: 'SMB share' }
                ]}
                displayExpr="text"
                valueExpr="value"
                value={kind}
                onValueChanged={(e) => {
                  setKind(e.value as StorageMountKind);
                  setTestResult(null);
                }}
                width={220}
              />
            </div>

            {kind === 'Smb' ? (
              <>
                <div>
                  <span className="client-space-wizard__field-label">UNC path</span>
                  <TextBox
                    value={smb.uncPath}
                    onValueChanged={(e) => setSmb({ ...smb, uncPath: String(e.value ?? '') })}
                    placeholder="\\\\server\\share\\ClientFolder"
                  />
                </div>
                <div className="client-space-wizard__grid-2">
                  <div>
                    <span className="client-space-wizard__field-label">Domain</span>
                    <TextBox
                      value={smb.domain}
                      onValueChanged={(e) => setSmb({ ...smb, domain: String(e.value ?? '') })}
                    />
                  </div>
                  <div>
                    <span className="client-space-wizard__field-label">Username</span>
                    <TextBox
                      value={smb.username}
                      onValueChanged={(e) => setSmb({ ...smb, username: String(e.value ?? '') })}
                    />
                  </div>
                </div>
                <div>
                  <span className="client-space-wizard__field-label">Password</span>
                  <TextBox
                    mode="password"
                    value={smb.password}
                    onValueChanged={(e) => setSmb({ ...smb, password: String(e.value ?? '') })}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="client-space-wizard__grid-2">
                  <div>
                    <span className="client-space-wizard__field-label">Bucket name</span>
                    <TextBox
                      value={s3.bucketName}
                      onValueChanged={(e) => setS3({ ...s3, bucketName: String(e.value ?? '') })}
                    />
                  </div>
                  <div>
                    <span className="client-space-wizard__field-label">Key prefix</span>
                    <TextBox
                      value={s3.keyPrefix}
                      onValueChanged={(e) => setS3({ ...s3, keyPrefix: String(e.value ?? '') })}
                      placeholder="e.g. SPR Files/DEMOCE1/"
                    />
                    <span className="client-space-wizard__hint">
                      Point this at the client&apos;s own folder so its people only see their space.
                    </span>
                  </div>
                </div>
                <div className="client-space-wizard__grid-2">
                  <div>
                    <span className="client-space-wizard__field-label">Service URL</span>
                    <TextBox
                      value={s3.serviceUrl}
                      onValueChanged={(e) => setS3({ ...s3, serviceUrl: String(e.value ?? '') })}
                      placeholder="Blank for AWS S3"
                    />
                  </div>
                  <div>
                    <span className="client-space-wizard__field-label">Region</span>
                    <TextBox
                      value={s3.region}
                      onValueChanged={(e) => setS3({ ...s3, region: String(e.value ?? '') })}
                    />
                  </div>
                </div>
                <div className="client-space-wizard__grid-2">
                  <div>
                    <span className="client-space-wizard__field-label">Access key ID</span>
                    <TextBox
                      value={s3.accessKeyId}
                      onValueChanged={(e) => setS3({ ...s3, accessKeyId: String(e.value ?? '') })}
                    />
                  </div>
                  <div>
                    <span className="client-space-wizard__field-label">Secret access key</span>
                    <TextBox
                      mode="password"
                      value={s3.secretAccessKey}
                      onValueChanged={(e) => setS3({ ...s3, secretAccessKey: String(e.value ?? '') })}
                    />
                  </div>
                </div>
                <CheckBox
                  text="Force path-style URLs (needed for MinIO)"
                  value={s3.forcePathStyle}
                  onValueChanged={(e) => setS3({ ...s3, forcePathStyle: Boolean(e.value) })}
                />
              </>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Button text="Test connection" onClick={handleTest} disabled={!connectionComplete || testing} />
              {testing && <LoadIndicator height={18} width={18} />}
              {testResult && (
                <span
                  style={{
                    fontSize: 13,
                    color: testResult.success
                      ? 'var(--dx-color-success, #16a34a)'
                      : 'var(--dx-color-danger, #dc2626)'
                  }}
                >
                  {testResult.message}
                </span>
              )}
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <p className="client-space-wizard__hint" style={{ marginTop: 0 }}>
              Who inside HVG can reach this space from the operations portal. These are ops roles and hub
              Entra groups. ItSysAdmin, OpsFinance, and OpsSales are pre-filled; remove any you don&apos;t
              need.
            </p>
            <AccessEditor
              roles={opsRoles}
              rows={opsAccess}
              onChange={setOpsAccess}
              groupStore={opsGroupStore}
              groupCache={opsGroupCache.current}
              groupPlaceholder="Search HVG hub groups…"
              emptyHint="No bindings yet — only ItSysAdmin and global override groups would see this mount."
            />
          </>
        )}

        {step === 3 && (
          <>
            <p className="client-space-wizard__hint" style={{ marginTop: 0 }}>
              Who at the client can reach this space, using SPR roles and groups from {tenantLabel}. The
              groups you pick here are also what @mentions resolve to on both sites.
            </p>
            <AccessEditor
              roles={clientRoles}
              rows={clientAccess}
              onChange={setClientAccess}
              groupStore={clientGroupStore}
              groupCache={clientGroupCache.current}
              groupPlaceholder="Search client tenant groups…"
              emptyHint="No bindings yet — without a group here, @mentions will have nobody to offer."
            />
          </>
        )}

        {step === 4 && (
          <dl className="client-space-wizard__review">
            <dt>Client tenant</dt>
            <dd>{tenantLabel}</dd>
            <dt>Mount name</dt>
            <dd>
              {displayName || '—'}
              {opsDisplayName && ` (WebOps: ${opsDisplayName})`}
            </dd>
            <dt>Storage</dt>
            <dd>
              {kind === 'Smb'
                ? smb.uncPath || '—'
                : `${s3.bucketName || '—'}${s3.keyPrefix ? ` / ${s3.keyPrefix}` : ''}`}
            </dd>
            <dt>Ops access</dt>
            <dd>
              {opsAccess.length === 0
                ? 'None — ItSysAdmin only'
                : opsAccess
                    .map((a) => `${a.role} · ${a.groupDisplayName ?? 'any group'} · ${a.mode}`)
                    .join(' | ')}
            </dd>
            <dt>Client access</dt>
            <dd>
              {clientAccess.length === 0
                ? 'None — SPR-Admin only, and @mentions will be empty'
                : clientAccess
                    .map((a) => `${a.role} · ${a.groupDisplayName ?? 'any group'} · ${a.mode}`)
                    .join(' | ')}
            </dd>
          </dl>
        )}
      </div>

      <div className="client-space-wizard__footer">
        <Button text="Back" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0 || submitting} />
        {step < STEPS.length - 1 ? (
          <Button
            text="Next"
            type="default"
            onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
            disabled={!canAdvance}
          />
        ) : (
          <Button
            text={submitting ? 'Creating…' : 'Create both mounts'}
            type="default"
            onClick={handleSubmit}
            disabled={submitting || !tenantId || !displayName.trim() || !connectionComplete}
          />
        )}
      </div>
      </div>
    </div>
  );
}
